import React from "react";
import {
  Box,
  Container,
  Grid,
  Image,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  Heading,
  Badge,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  useToast,
  IconButton,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import {
  FiShoppingBag,
  FiStar,
  FiCheckCircle,
  FiArrowLeft,
  FiDroplet,
  FiClock,
  FiShield,
  FiHeart,
} from "react-icons/fi";
import { 
  FaMagic, 
  FaLeaf,
  FaGem,
  FaRegClock,
  FaSprayCan,
  FaCertificate,
  FaShippingFast,
} from "react-icons/fa";
import type { Product } from "../types";
import { getProductById } from "../data/products";

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const toast = useToast();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const product = getProductById(id || "");

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product);
  };

  if (!product) {
    return (
      <Container maxW="1200px" py={8}>
        <VStack spacing={4} align="center">
          <Text fontSize="xl" color="gray.500">Product not found</Text>
          <Button colorScheme="purple" onClick={() => navigate("/products")}>
            Return to Products
          </Button>
        </VStack>
      </Container>
    );
  }

  const isInStock = Number(product.stock) > 0;

  return (
    <Container maxW="1400px" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Back Button */}
        <HStack>
          <IconButton
            aria-label="Go back"
            icon={<FiArrowLeft />}
            onClick={() => navigate(-1)}
            variant="ghost"
            color="purple.500"
          />
          <Text color="purple.500">Back to Products</Text>
        </HStack>

        {/* Product Details */}
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={8}>
          {/* Product Image */}
          <Box position="relative">
            <Box
              position="relative"
              borderRadius="xl"
              overflow="hidden"
              boxShadow="xl"
              bg="white"
              p={4}
            >
              <Image
                src={product.image}
                alt={product.name}
                borderRadius="lg"
                width="100%"
                height="auto"
                objectFit="contain"
                transition="transform 0.3s"
                _hover={{ transform: "scale(1.05)" }}
              />
              <Badge
                position="absolute"
                top={4}
                right={4}
                colorScheme="purple"
                fontSize="md"
                px={4}
                py={2}
                borderRadius="full"
              >
                {product.discount}% OFF
              </Badge>
            </Box>
            
            {/* Product Features Grid */}
            <SimpleGrid columns={2} spacing={4} mt={6}>
              <Flex align="center" gap={2}>
                <Icon as={FaCertificate} color="purple.500" />
                <Text>Authentic Product</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Icon as={FaShippingFast} color="purple.500" />
                <Text>Free Shipping</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Icon as={FiDroplet} color="purple.500" />
                <Text>100ml Volume</Text>
              </Flex>
              <Flex align="center" gap={2}>
                <Icon as={FiClock} color="purple.500" />
                <Text>Long Lasting</Text>
              </Flex>
            </SimpleGrid>
          </Box>

          {/* Product Info */}
          <VStack align="start" spacing={6}>
            <VStack align="start" spacing={2}>
              <Heading size="xl" color="purple.700">{product.name}</Heading>
              <HStack>
                <Icon as={FiStar} color="yellow.400" />
                <Text fontWeight="bold">{product.rating} / 5.0</Text>
                <Text color="gray.500">({product.sold} sold)</Text>
              </HStack>
            </VStack>

            <Divider />

            <VStack align="start" spacing={2}>
              <HStack>
                <Text fontSize="3xl" fontWeight="bold" color="purple.500">
                  Rs. {product.price.toLocaleString()}
                </Text>
                {product.originalPrice && (
                  <Text
                    fontSize="xl"
                    textDecoration="line-through"
                    color="purple.300"
                  >
                    Rs. {product.originalPrice.toLocaleString()}
                  </Text>
                )}
              </HStack>
              <Text color="gray.600" fontSize="lg">{product.description}</Text>
            </VStack>

            <Divider />

            {/* Fragrance Notes */}
            <VStack align="start" spacing={4} width="100%">
              <Heading size="md" color="purple.700">Fragrance Notes</Heading>
              <SimpleGrid columns={3} spacing={4} width="100%">
                <Box p={4} borderWidth="1px" borderRadius="lg" borderColor="purple.200">
                  <Text fontWeight="bold" color="purple.500">Top Notes</Text>
                  <Text>{product.features[0].replace("Top notes: ", "")}</Text>
                </Box>
                <Box p={4} borderWidth="1px" borderRadius="lg" borderColor="purple.200">
                  <Text fontWeight="bold" color="purple.500">Heart Notes</Text>
                  <Text>{product.features[1].replace("Heart notes: ", "")}</Text>
                </Box>
                <Box p={4} borderWidth="1px" borderRadius="lg" borderColor="purple.200">
                  <Text fontWeight="bold" color="purple.500">Base Notes</Text>
                  <Text>{product.features[2].replace("Base notes: ", "")}</Text>
                </Box>
              </SimpleGrid>
            </VStack>

            <Divider />

            {/* Specifications */}
            <VStack align="start" spacing={4} width="100%">
              <Heading size="md" color="purple.700">Specifications</Heading>
              <SimpleGrid columns={2} spacing={4} width="100%">
                <Box>
                  <Text fontWeight="bold" color="purple.500">Brand</Text>
                  <Text>{product.specifications?.brand}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="purple.500">Volume</Text>
                  <Text>{product.specifications?.volume}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="purple.500">Type</Text>
                  <Text>{product.specifications?.type}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="purple.500">Concentration</Text>
                  <Text>{product.specifications?.concentration}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold" color="purple.500">Gender</Text>
                  <Text>{product.specifications?.gender}</Text>
                </Box>
              </SimpleGrid>
            </VStack>

            <Divider />

            {/* Stock Status and Add to Cart */}
            <VStack align="start" spacing={4} width="100%">
              <HStack justify="space-between" width="100%">
                <VStack align="start">
                  <Text fontWeight="bold">Stock Status</Text>
                  <Text color={isInStock ? "purple.500" : "red.500"}>
                    {isInStock ? "In Stock" : "Out of Stock"}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {product.stock} units available
                  </Text>
                </VStack>
                <IconButton
                  aria-label="Add to wishlist"
                  icon={<FiHeart />}
                  variant="ghost"
                  color="purple.500"
                  size="lg"
                />
              </HStack>

              <Button
                bg="purple.500"
                color="white"
                size="lg"
                leftIcon={<FiShoppingBag />}
                onClick={handleAddToCart}
                isDisabled={!isInStock}
                _hover={{
                  bg: "purple.600",
                  transform: "translateY(-3px)",
                  shadow: "xl",
                  boxShadow: "0 4px 20px rgba(128, 90, 213, 0.6)",
                }}
                _active={{
                  transform: "translateY(0)",
                  boxShadow: "0 2px 10px rgba(128, 90, 213, 0.4)",
                }}
                borderRadius="full"
                px={10}
                py={6}
                fontWeight="bold"
                letterSpacing="wider"
                fontSize="lg"
                transition="all 0.2s"
                border="2px solid"
                borderColor="purple.400"
                width="100%"
              >
                Add to Cart
              </Button>
            </VStack>
          </VStack>
        </Grid>

        {/* Additional Information Tabs */}
        <Box mt={8}>
          <Tabs variant="enclosed" colorScheme="purple">
            <TabList>
              <Tab>Product Details</Tab>
              <Tab>Shipping Information</Tab>
              <Tab>Return Policy</Tab>
              <Tab>Reviews</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <VStack align="start" spacing={4}>
                  <Text>{product.description}</Text>
                  <List spacing={3}>
                    {product.features.map((feature, index) => (
                      <ListItem key={index}>
                        <ListIcon as={FaMagic} color="purple.400" />
                        <Text as="span">{feature}</Text>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </TabPanel>
              <TabPanel>
                <VStack align="start" spacing={4}>
                  <Text>• Free shipping on orders above Rs. 5000</Text>
                  <Text>• Delivery within 3-5 business days</Text>
                  <Text>• Secure packaging to protect your fragrance</Text>
                  <Text>• Tracking number provided for all orders</Text>
                </VStack>
              </TabPanel>
              <TabPanel>
                <VStack align="start" spacing={4}>
                  <Text>• 30-day return policy for unopened items</Text>
                  <Text>• Full refund or exchange available</Text>
                  <Text>• Return shipping costs covered by us</Text>
                  <Text>• Hassle-free return process</Text>
                </VStack>
              </TabPanel>
              <TabPanel>
                <VStack align="start" spacing={4}>
                  <Text>• Based on {product.sold} verified purchases</Text>
                  <Text>• Average rating: {product.rating}/5.0</Text>
                  <Text>• Customer reviews coming soon</Text>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Container>
  );
};

export default ProductDetail;
